import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { ApiError } from '@/api/http/types'
import {
  applySignupVerifySession,
  useResendSignupOtp,
  useVerifySignupOtp,
} from '../api'
import { SP_OTP_EXPIRED_MESSAGE } from '../lib'
import { lockoutFromApiError } from '../lib/lockout'
import {
  isValidSignupVerifySessionId,
  normalizeSignupVerifySessionId,
  parseSignupVerifyOtpFromSearch,
  signupVerifyCleanSearch,
  signupVerifyUrlNeedsCleaning,
} from '../lib/signup-verify-url'
import { LockoutCountdown, SignupOtpInput } from '../components'
import { useLockoutRemaining } from '../hooks/useLockoutRemaining'
import {
  signupVerifyCardSx,
  signupVerifyContentColumnSx,
  signupVerifyHeadingFrameSx,
  signupVerifyHeadingSx,
  signupVerifyLogoBlockSx,
  signupVerifyLogoImgSx,
  signupVerifyOtpLabelSx,
  signupVerifyPrimaryButtonSx,
  signupVerifyResendLinkSx,
  signupVerifySubtitleSx,
  signupVerifyTitleBlockSx,
} from '../styles/signupVerifyStyles'

const RESEND_COOLDOWN_SECONDS = 120

/** Figma 5074-11964 (empty OTP) / 5074-12873 (filled OTP) — centered AuthLayout (no marketing panel) */
export function SignupVerifyPage() {
  useDocumentTitle('Verify Identity')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const sessionId = normalizeSignupVerifySessionId(searchParams.toString())
  const initialOtpRef = useRef(parseSignupVerifyOtpFromSearch(searchParams.toString()))

  const verifySignup = useVerifySignupOtp()
  const resendSignup = useResendSignupOtp()

  const [code, setCode] = useState(() => initialOtpRef.current)
  const [serverError, setServerError] = useState<string | null>(null)
  const [lockoutUntilMs, setLockoutUntilMs] = useState<number | null>(null)
  const [resendCooldownUntilMs, setResendCooldownUntilMs] = useState<number | null>(
    null
  )
  const lockoutRemaining = useLockoutRemaining(lockoutUntilMs)
  const resendRemaining = useLockoutRemaining(resendCooldownUntilMs)
  const isLockedOut = lockoutRemaining > 0
  const resendDisabled =
    resendRemaining > 0 || resendSignup.isPending || isLockedOut || !sessionId

  useEffect(() => {
    if (!sessionId || !isValidSignupVerifySessionId(sessionId)) {
      navigate('/login', { replace: true })
      return
    }
    if (signupVerifyUrlNeedsCleaning(searchParams.toString())) {
      setSearchParams(new URLSearchParams(signupVerifyCleanSearch(sessionId)), {
        replace: true,
      })
    }
  }, [sessionId, searchParams, navigate, setSearchParams])

  if (!sessionId || !isValidSignupVerifySessionId(sessionId)) {
    return null
  }

  const runVerify = (otpCode: string) => {
    if (otpCode.length !== 6 || isLockedOut) return
    setServerError(null)
    verifySignup.mutate(
      { signupSessionId: sessionId, code: otpCode },
      {
        onSuccess: (data) => {
          if (!data?.loggedUser) {
            setServerError('Verification failed. Please try again.')
            return
          }
          applySignupVerifySession(data)
          // Navigate immediately — do not wait on /me (cookies + loggedUser from verify).
          navigate('/', { replace: true, state: { fromSignupVerify: true } })
        },
        onError: (error) => {
          if (error instanceof ApiError) {
            const lockout = lockoutFromApiError(error)
            if (lockout) {
              setLockoutUntilMs(lockout.lockedUntilMs)
              setServerError(error.message)
              return
            }
            if (error.status === 410) {
              setServerError(SP_OTP_EXPIRED_MESSAGE)
              return
            }
            setServerError(error.message)
            return
          }
          setServerError('An unexpected error occurred. Please try again.')
        },
      }
    )
  }

  const handleResend = () => {
    if (resendDisabled) return
    setServerError(null)
    resendSignup.mutate(
      { signupSessionId: sessionId },
      {
        onSuccess: () => {
          setCode('')
          setResendCooldownUntilMs(Date.now() + RESEND_COOLDOWN_SECONDS * 1000)
        },
        onError: (error) => {
          if (error instanceof ApiError) {
            const lockout = lockoutFromApiError(error)
            if (lockout?.retryAfterSeconds) {
              setResendCooldownUntilMs(
                Date.now() + lockout.retryAfterSeconds * 1000
              )
            }
            setServerError(error.message)
          }
        },
      }
    )
  }

  const canSubmit = code.length === 6 && !isLockedOut && !verifySignup.isPending

  return (
    <Box
      data-testid="signup-verify-page"
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Box data-testid="signup-verify-card" sx={signupVerifyCardSx}>
        <Box sx={signupVerifyLogoBlockSx}>
          <Box
            component="img"
            src={brushfireLogo}
            alt="Brushfire"
            sx={signupVerifyLogoImgSx}
          />
        </Box>

        <Box sx={signupVerifyTitleBlockSx} data-testid="signup-verify-title-block">
          <Box sx={signupVerifyHeadingFrameSx}>
            <Typography component="h1" sx={signupVerifyHeadingSx}>
              Verify Your Identity
            </Typography>
          </Box>
          <Typography component="p" sx={signupVerifySubtitleSx}>
            We have sent a verification code to your registered email
          </Typography>
        </Box>

        <Typography component="p" sx={signupVerifyOtpLabelSx}>
          Enter verification code
        </Typography>

        <Box sx={signupVerifyContentColumnSx}>
          {serverError && (
            <Typography
              role="alert"
              data-testid="signup-verify-error"
              sx={{ color: '#ef4444', fontSize: '14px', textAlign: 'center', m: 0 }}
            >
              {serverError}
            </Typography>
          )}

          {lockoutUntilMs && (
            <LockoutCountdown
              lockedUntilMs={lockoutUntilMs}
              message="Too many incorrect attempts. Please wait before trying again."
            />
          )}

          <SignupOtpInput
            value={code}
            onChange={setCode}
            disabled={isLockedOut || verifySignup.isPending}
            error={!!serverError && !isLockedOut}
          />

          <Button
            type="button"
            variant="contained"
            fullWidth
            disableElevation
            disabled={!canSubmit}
            onClick={() => runVerify(code)}
            data-testid="signup-verify-submit"
            sx={signupVerifyPrimaryButtonSx}
          >
            {verifySignup.isPending ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Verify'
            )}
          </Button>

          <Button
            type="button"
            variant="text"
            onClick={handleResend}
            disabled={resendDisabled}
            data-testid="signup-verify-resend"
            sx={signupVerifyResendLinkSx}
          >
            {resendRemaining > 0
              ? `Resend OTP in ${resendRemaining}s`
              : 'Resend OTP'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
